<div class="bill-details-dialog">
  <div class="dialog-header">
    <h2 mat-dialog-title>Bill Details</h2>
    <button mat-icon-button (click)="close()" class="close-button">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <mat-dialog-content class="dialog-content">
    <div class="bill-details-container">
      <!-- Header Section -->
      <div class="section-header">
        <h3>Bill Information</h3>
      </div>

      <div class="details-grid">
        <div class="detail-item">
          <label>Bill ID</label>
          <span>#{{ bill.id }}</span>
        </div>
        <div class="detail-item">
          <label>Invoice Number</label>
          <span>{{ bill.invoice_number || 'N/A' }}</span>
        </div>
        <div class="detail-item">
          <label>Project Name</label>
          <span>{{ bill.project_name }}</span>
        </div>
        <div class="detail-item">
          <label>Project Type</label>
          <span [class]="'px-2 py-1 rounded-full text-xs font-medium ' + getStatusClass(bill.status)">
            {{ getProjectTypeLabel(bill.project_type) }}
          </span>
        </div>
        <div class="detail-item">
          <label>Domain</label>
          <span>{{ bill.domain }}{{ bill.sub_domain ? ' - ' + bill.sub_domain : '' }}</span>
        </div>
        <div class="detail-item">
          <label>Status</label>
          <span [class]="'px-2 py-1 rounded-full text-xs font-medium ' + getStatusClass(bill.status)">
            {{ bill.status | titlecase }}
          </span>
        </div>
      </div>

      <!-- Billing Details -->
      <div class="section-header" style="margin-top: 24px;">
        <h3>Billing Details</h3>
      </div>

      <div class="details-grid">
        <div class="detail-item">
          <label>Quantity</label>
          <span>{{ formatQuantity(bill.quantity, bill.rate_type) }}</span>
        </div>
        <div class="detail-item">
          <label>Area</label>
          <span>{{ bill.area | number:'1.2-2' }} sqm</span>
        </div>
        <div class="detail-item" *ngIf="bill.project_type === 'ROAD' && getRoadLength()">
          <label>Road Length</label>
          <span>{{ formatRoadLength() }}</span>
        </div>
        <div class="detail-item">
          <label>Unit Rate</label>
          <span>{{ formatCurrency(bill.unit_rate) }} 
            <small class="text-gray-500">
              ({{ bill.rate_type === 'per_sq_mt' ? 'per sq mt' : bill.rate_type === 'per_psm' ? 'per PSM' : 'per project' }})
            </small>
          </span>
        </div>
        <div class="detail-item">
          <label>Base Rate</label>
          <span>{{ formatCurrency(bill.base_rate) }}</span>
        </div>
        <div class="detail-item full-width">
          <label>Bill Amount</label>
          <div class="flex flex-col gap-1">
          <span class="text-2xl font-bold text-blue-600">{{ formatCurrency(bill.bill_amount) }}</span>
            <span class="text-xs text-gray-500" *ngIf="isAmountManuallyUpdated()">
              (Manually updated - Calculated: {{ formatCurrency(getCalculatedAmount()) }})
            </span>
          </div>
        </div>
      </div>

      <!-- Road Project Specific Details -->
      <div class="section-header" style="margin-top: 24px;" *ngIf="bill.project_type === 'ROAD'">
        <h3>Road Project Details</h3>
      </div>

      <div class="details-grid" *ngIf="bill.project_type === 'ROAD'">
        <div class="detail-item" *ngIf="getRoadLength()">
          <label>Road Length</label>
          <span>{{ formatRoadLength() }}</span>
        </div>
        <div class="detail-item" *ngIf="calculateNumberOfLanes()">
          <label>Number of Lanes</label>
          <span>{{ calculateNumberOfLanes() }}</span>
        </div>
        <div class="detail-item">
          <label>Lane Width</label>
          <span>3.75 m (per lane)</span>
        </div>
        <div class="detail-item full-width" *ngIf="calculateNumberOfLanes() && getRoadLength()">
          <label>Area Calculation</label>
          <span class="text-sm text-gray-600">
            3.75 m × {{ calculateNumberOfLanes() }} lanes × {{ getRoadLength()?.toLocaleString('en-IN', { maximumFractionDigits: 2 }) }} m = {{ formatQuantity(bill.quantity, bill.rate_type) }}
          </span>
        </div>
        <div class="detail-item full-width" *ngIf="calculateNumberOfLanes() && getRoadLength()">
          <label>Calculated Bill Amount</label>
          <span class="text-sm text-gray-600">
            {{ formatQuantity(bill.quantity, bill.rate_type) }} × {{ formatCurrency(bill.unit_rate) }} = {{ formatCurrency(getCalculatedAmount()) }}
          </span>
        </div>
      </div>

      <!-- Dates -->
      <div class="section-header" style="margin-top: 24px;">
        <h3>Important Dates</h3>
      </div>

      <div class="details-grid">
        <div class="detail-item">
          <label>Billing Date</label>
          <span>{{ formatDate(bill.billing_date) }}</span>
        </div>
        <div class="detail-item">
          <label>Payment Date</label>
          <span>{{ formatDate(bill.payment_date) }}</span>
        </div>
        <div class="detail-item">
          <label>Created At</label>
          <span>{{ formatDate(bill.created_at) }}</span>
        </div>
        <div class="detail-item" *ngIf="bill.updated_at">
          <label>Last Updated</label>
          <span>{{ formatDateTime(bill.updated_at) }}</span>
        </div>
      </div>

      <!-- Organization Details -->
      <div class="section-header" style="margin-top: 24px;" *ngIf="bill.org_name">
        <h3>Organization Details</h3>
      </div>

      <div class="details-grid" *ngIf="bill.org_name">
        <div class="detail-item">
          <label>Organization</label>
          <span>{{ bill.org_name }}</span>
        </div>
        <div class="detail-item" *ngIf="bill.org_address">
          <label>Address</label>
          <span>{{ bill.org_address }}</span>
        </div>
        <div class="detail-item" *ngIf="bill.org_gst">
          <label>GSTIN</label>
          <span>{{ bill.org_gst }}</span>
        </div>
        <div class="detail-item" *ngIf="bill.org_pan">
          <label>PAN</label>
          <span>{{ bill.org_pan }}</span>
        </div>
        <div class="detail-item" *ngIf="bill.org_email">
          <label>Email</label>
          <span>{{ bill.org_email }}</span>
        </div>
        <div class="detail-item" *ngIf="bill.org_phone">
          <label>Phone</label>
          <span>{{ bill.org_phone }}</span>
        </div>
      </div>

      <!-- Amount Change History (if available) -->
      <div class="section-header" style="margin-top: 24px;" *ngIf="getAmountHistory().length > 0">
        <h3>Amount Change History</h3>
      </div>

      <div class="amount-history" *ngIf="getAmountHistory().length > 0">
        <div class="history-item" *ngFor="let change of getAmountHistory().slice().reverse()">
          <div class="history-header">
            <span class="history-date">{{ formatDateTime(change.updated_at) }}</span>
            <span class="history-user">Updated by: {{ change.updated_by_name || 'Superadmin' }}</span>
          </div>
          <div class="history-details">
            <div class="history-amounts">
              <span class="amount-change">
                <span class="amount-label">From:</span>
                <span class="amount-value">{{ formatCurrency(change.original_amount) }}</span>
              </span>
              <mat-icon class="arrow-icon">arrow_forward</mat-icon>
              <span class="amount-change">
                <span class="amount-label">To:</span>
                <span class="amount-value new-amount">{{ formatCurrency(change.updated_amount) }}</span>
              </span>
            </div>
            <div class="history-remark" *ngIf="change.remark">
              <strong>Remark:</strong> {{ change.remark }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </mat-dialog-content>

  <mat-dialog-actions align="end" class="dialog-actions">
    <button mat-button (click)="close()">Close</button>
  </mat-dialog-actions>
</div>

